import arrayMutators from 'final-form-arrays';
import { uniqueId } from 'lodash';
import React, { useState } from 'react';
import { Form } from 'react-final-form';
import styled, { css } from 'styled-components';
import * as yup from 'yup';

import validateYupSchema from 'common/utils/validateYupSchema';
import MenuEditorConfirmationPrompt from 'modules/menu/components/MenuEditorConfirmationPrompt';
import { SelectionCategory } from 'modules/shared-menu/types';

import { AddCategoryDetailsFormSection } from './AddCategoryDetailsFormSection';
import { AddCategoryFormSection } from './AddCategoryFormSection';
import { UNIQUEID_CATEGORY_PREFIX } from './constants';
import { AddCategoriesFormValues } from './types';
import { categoriesValidationSchema, childCategoriesValidationSchema } from './validationSchema';

type CreateCategoryFormProps = {
  onCloseClick: () => void;
  onSubmit: (values: AddCategoriesFormValues) => Promise<SelectionCategory[]>;
};

type Step = 'ADD_CATEGORY' | 'ADD_CATEGORY_DETAILS';

const Root = styled.div<{ disabled: boolean }>`
  height: 100%;
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.8;
      pointer-events: none;
    `}
`;

const createCategoryFormValidationSchema = yup.object({
  categories: categoriesValidationSchema,
  childCategoriesRecord: childCategoriesValidationSchema,
});

const initialCategoryRowId = uniqueId(UNIQUEID_CATEGORY_PREFIX);

const initialValues: AddCategoriesFormValues = {
  categories: [
    {
      name: '',
      description: '',
      image: { type: 'no-image' },
      _rowId: initialCategoryRowId,
    },
  ],
  childCategoriesRecord: {
    [initialCategoryRowId]: [
      {
        name: '',
        _rowId: uniqueId(UNIQUEID_CATEGORY_PREFIX),
      },
    ],
  },
};

export const AddCategoriesForm = ({ onCloseClick, onSubmit }: CreateCategoryFormProps) => {
  const [step, setStep] = useState<Step>('ADD_CATEGORY');

  return (
    <Form<AddCategoriesFormValues>
      mutators={{
        ...arrayMutators,
      }}
      onSubmit={onSubmit}
      initialValues={initialValues}
      validate={validateYupSchema(createCategoryFormValidationSchema)}
    >
      {({ handleSubmit, submitting, dirty }) => (
        <>
          <Root disabled={submitting}>
            {step === 'ADD_CATEGORY' && (
              <AddCategoryFormSection
                onCloseClick={onCloseClick}
                onCancelClick={onCloseClick}
                onNextClick={() => {
                  setStep('ADD_CATEGORY_DETAILS');
                }}
              />
            )}
            {step === 'ADD_CATEGORY_DETAILS' && (
              <AddCategoryDetailsFormSection
                onCloseClick={onCloseClick}
                onSaveClick={handleSubmit}
                onBackClick={() => {
                  setStep('ADD_CATEGORY');
                }}
              />
            )}
          </Root>

          <MenuEditorConfirmationPrompt when={dirty && !submitting} />
        </>
      )}
    </Form>
  );
};
